import { Fullscreen, Minimize, LucideIcon } from "lucide-react";
import { Button, ButtonProps } from "../../button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from "../../breadcrumb";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem } from "../../dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../tooltip";

export interface ExpandableDialogHeaderAction extends Omit<ButtonProps, "children" | "hoverLabel"> {
  icon: LucideIcon;
  hoverLabel: string;
}

export interface SectionHeaderProps {
  breadcrumbs: Array<{
    title: string;
    onClick?: () => void;
  }>;
  expanded: boolean;
  toggleExpanded: () => void;
  hideExpandToggle?: boolean;
  trailingText?: string;
  actions?: Array<{
    icon: LucideIcon;
    onClick: () => void;
    label?: string;
  }>;
  headerActions?: ExpandableDialogHeaderAction[];
}

/**
 * Header for a section within ExpandableDialogWithSidebar, displaying breadcrumb navigation and expand/minimize button.
 *
 * Displays hierarchical breadcrumb navigation based on the current section/subsection selection.
 * - First breadcrumb item is always the dialog title (non-clickable, max-width 150px with truncation)
 * - Intermediate breadcrumbs are clickable if onClick is provided (for navigating to parent sections)
 * - Last breadcrumb is always the current page (non-clickable)
 * - Breadcrumb separators are hidden on mobile, visible on medium+ screens
 *
 * Optionally displays trailing text and action buttons after the breadcrumbs.
 *
 * The toggle button switches between fullscreen and minimized dialog states.
 * The button icon changes based on the expanded state (Fullscreen when minimized, Minimize when expanded).
 *
 * @param breadcrumbs - Array of breadcrumb items with title and optional onClick handler
 * @param expanded - Whether the dialog is currently in expanded/fullscreen mode
 * @param toggleExpanded - Callback to toggle between expanded and normal states
 * @param trailingText - Optional text to display after the breadcrumbs
 * @param actions - Optional array of action buttons (icon buttons) to display after breadcrumbs/trailing text
 */
export const SectionHeader = ({
  breadcrumbs,
  expanded,
  toggleExpanded,
  hideExpandToggle,
  trailingText,
  actions,
  headerActions,
}: SectionHeaderProps) => {
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
      <div className="flex items-center gap-2 pr-3 flex-1 min-w-0">
        {!hideExpandToggle && (
          <Button
            variant="ghost"
            onClick={toggleExpanded}
            className="size-6 p-0 flex-shrink-0"
            data-testid="section-header-toggle"
          >
            {expanded ? (
              <Minimize className="h-4 w-4" data-testid="minimize-icon" />
            ) : (
              <Fullscreen className="h-4 w-4" data-testid="fullscreen-icon" />
            )}
          </Button>
        )}
        <Breadcrumb data-testid="section-breadcrumb" className="flex-shrink min-w-0 flex-1">
          <BreadcrumbList>
            {breadcrumbs.length > (expanded ? 5 : 3) ? (
              <>
                <BreadcrumbItem
                  data-testid="dialog-title-breadcrumb"
                  title={breadcrumbs[0]?.title}
                  className="truncate"
                >
                  <BreadcrumbPage>{breadcrumbs[0]?.title}</BreadcrumbPage>
                </BreadcrumbItem>

                <BreadcrumbSeparator
                  className="hidden md:block flex-shrink-0"
                  data-testid="dialog-breadcrumb-separator"
                />

                <BreadcrumbItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className="flex items-center gap-1"
                      data-testid="collapsed-breadcrumbs-dropdown"
                    >
                      <BreadcrumbEllipsis className="size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {breadcrumbs.slice(1, -1).map((crumb, index) => (
                        <DropdownMenuItem
                          key={index}
                          onClick={crumb.onClick}
                          className={crumb.onClick ? "cursor-pointer" : "cursor-default"}
                          title={crumb.title}
                        >
                          {crumb.title}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </BreadcrumbItem>

                <BreadcrumbSeparator
                  className="hidden md:block flex-shrink-0"
                  data-testid="dialog-breadcrumb-separator"
                />

                <BreadcrumbItem
                  data-testid="current-dialog-section-breadcrumb"
                  title={breadcrumbs[breadcrumbs.length - 1]?.title}
                  className="truncate"
                >
                  <BreadcrumbPage>{breadcrumbs[breadcrumbs.length - 1]?.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            ) : (
              breadcrumbs.map((crumb, index) => {
                const isReadonly = index === breadcrumbs.length - 1 || index === 0;

                return (
                  <span key={index} className="contents">
                    {isReadonly ? (
                      <BreadcrumbItem
                        data-testid={index === 0 ? "dialog-title-breadcrumb" : "current-dialog-section-breadcrumb"}
                        title={crumb.title}
                        className="truncate"
                      >
                        <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                      </BreadcrumbItem>
                    ) : (
                      <>
                        <BreadcrumbLink
                          onClick={crumb.onClick ? crumb.onClick : (e) => e.preventDefault()}
                          title={crumb.title}
                          className={
                            crumb.onClick
                              ? "max-w-[150px] truncate cursor-pointer"
                              : "cursor-default max-w-[150px] truncate"
                          }
                        >
                          {crumb.title}
                        </BreadcrumbLink>
                      </>
                    )}
                    {index !== breadcrumbs.length - 1 && (
                      <BreadcrumbSeparator
                        className="hidden md:block flex-shrink-0"
                        data-testid="dialog-breadcrumb-separator"
                      />
                    )}
                  </span>
                );
              })
            )}
          </BreadcrumbList>
        </Breadcrumb>
        {trailingText && (
          <span className="text-sm text-muted-foreground flex-shrink-0" data-testid="section-header-trailing-text">
            {trailingText}
          </span>
        )}
        {actions && actions.length > 0 && (
          <TooltipProvider>
            <div
              className="flex items-center gap-1 ml-2 flex-shrink-0 text-muted-foreground"
              data-testid="section-header-actions"
            >
              {actions.map((action, index) => {
                const button = (
                  <Button
                    key={index}
                    variant="ghost"
                    onClick={action.onClick}
                    className="size-6 p-0"
                    aria-label={action.label}
                  >
                    <action.icon className="h-4 w-4" />
                  </Button>
                );

                return action.label ? (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent>
                      <p>{action.label}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  button
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </div>
      {headerActions && headerActions.length > 0 && (
        <div className="flex items-center gap-1 flex-shrink-0 pr-6" data-testid="section-header-header-actions">
          {headerActions.map(({ icon: Icon, ...action }) => (
            <Button key={action.hoverLabel} variant="ghost" size="sm-icon" {...action}>
              <Icon />
            </Button>
          ))}
        </div>
      )}
    </header>
  );
};
